import React, { useMemo, useState } from "react";
import { ReactComponent as CopyIcon } from "../../../assets/img/dashboard-svgs/copy.svg";
import { ReactComponent as CheckIcon } from "../../../assets/img/dashboard-svgs/checkmark.svg";
import { ReactComponent as TrashIcon } from "../../../assets/img/dashboard-svgs/ob-trash.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/img/dashboard-svgs/ob-download.svg";

const ICON_COLORS = ["#6B46FF", "#00D4E4", "#F59E0B", "#10B981", "#F43F5E", "#4F46E5"];
const UPLOAD_TYPES = new Set(["image", "svg_image", "file_upload", "video"]);

const resolvedValue = (key, formValues, fieldMeta) => {
  const v = formValues[key];
  if (typeof v !== "undefined" && v !== null && v !== "") return v;
  const meta = fieldMeta[key];
  if (meta?.uploaded && typeof meta.uploaded === "string") return meta.uploaded;
  if (meta?.file_url && typeof meta.file_url === "string") return meta.file_url;
  return "";
};

const parseOptions = (options) => {
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === "string") {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return options.split(",").map((item) => item.trim()).filter(Boolean);
    }
  }
  if (typeof options === "object") {
    return Object.keys(options).map((key) => ({ value: key, label: options[key] }));
  }
  return [];
};

const getCharLimitInfo = (field) => {
  const charLimits = field.char_limits || {};
  if (!charLimits || typeof charLimits !== "object" || Object.keys(charLimits).length === 0) {
    return { limits: null, smallestPlatform: null, smallestLimit: null };
  }

  const entries = Object.entries(charLimits).map(([platform, limit]) => [platform, Number(limit)]);
  let smallestPlatform = null;
  let smallestLimit = Infinity;

  entries.forEach(([platform, limit]) => {
    if (Number.isFinite(limit) && limit > 0 && limit < smallestLimit) {
      smallestLimit = limit;
      smallestPlatform = platform;
    }
  });

  return {
    limits: charLimits,
    smallestPlatform: smallestPlatform || null,
    smallestLimit: Number.isFinite(smallestLimit) ? smallestLimit : null,
  };
};

const getUploadedFiles = (key, formValues, fieldMeta) => {
  const meta = fieldMeta[key] || {};
  const value = formValues[key];
  let files = [];

  if (Array.isArray(meta?.file_items) && meta.file_items.length > 0) {
    meta.file_items.forEach((item) => {
      if (item && typeof item.value === "string" && item.value) {
        files.push({
          url: item.value,
          name: item.value.split("/").pop() || "File",
          value_id: item.value_id != null ? item.value_id : null,
        });
      }
    });
    if (files.length > 0) return files;
  }

  if (Array.isArray(meta?.file_urls)) {
    meta.file_urls.forEach((url) => {
      if (typeof url === "string" && url) {
        files.push({ url, name: url.split("/").pop() || "File", value_id: null });
      }
    });
  }

  if (typeof value === "string" && value.startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        parsed.forEach((item) => {
          const url = typeof item === "string" ? item : (typeof item?.url === "string" ? item.url : (typeof item?.file_url === "string" ? item.file_url : ""));
          if (url && !files.some((f) => f.url === url)) {
            files.push({
              url,
              name: (typeof item === "object" && (item.file_name || item.name)) || url.split("/").pop(),
              value_id: (typeof item === "object" && item.value_id != null) ? item.value_id : null,
            });
          }
        });
      }
    } catch (e) { /* not JSON */ }
  }

  const rawSingle = meta.uploaded || meta.file_url || "";
  const singleUrl = typeof rawSingle === "string" ? rawSingle : "";
  if (singleUrl && !files.some((f) => f.url === singleUrl)) {
    files.push({ url: singleUrl, name: meta.file_name || singleUrl.split("/").pop() || "File", value_id: null });
  }

  if (typeof value === "string" && value && !value.startsWith("[") && /\.(png|jpg|jpeg|gif|svg|webp|mp4|mov|pdf|zip)/i.test(value)) {
    if (!files.some((f) => f.url === value)) {
      files.push({ url: value, name: value.split("/").pop() || "File", value_id: null });
    }
  }

  return files;
};

const isScreenshotQuestion = (field) => {
  const slug = String(field.subsection_slug || "").toLowerCase();
  const prompt = String(field.prompt || "").toLowerCase();
  const key = String(field.key || "").toLowerCase();
  return /screenshot/.test(slug) || /screenshot/.test(prompt) || /screenshot/.test(key);
};

const validateMediaDimensions = async (file, field) => {
  const inputType = field?.input_type;
  const constraints = field?.media_constraints || {};

  if (!file || (!inputType?.includes("image") && inputType !== "video")) {
    return { valid: true };
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result;
      if (!src) return resolve({ valid: true });

      if (inputType === "video" || inputType?.startsWith("video")) {
        const video = document.createElement("video");
        video.onloadedmetadata = () => {
          const errors = [];
          const aspectRatio = video.videoWidth / video.videoHeight;
          if (constraints.aspect_ratio) {
            const [aspectW, aspectH] = String(constraints.aspect_ratio).split(":").map(Number);
            const expectedRatio = aspectW / aspectH;
            if (Math.abs(aspectRatio - expectedRatio) > 0.01) {
              errors.push(`Aspect ratio: ${constraints.aspect_ratio} (current: ${aspectRatio.toFixed(2)}:1)`);
            }
          }
          resolve({ valid: errors.length === 0, errors: errors.length > 0 ? errors : undefined, dimensions: { width: video.videoWidth, height: video.videoHeight } });
        };
        video.onerror = () => resolve({ valid: true });
        video.src = src;
      } else {
        const img = new Image();
        img.onload = () => {
          const errors = [];
          const resolution = `${img.naturalWidth}x${img.naturalHeight}`;
          if (constraints.resolution) {
            const allowed = Array.isArray(constraints.resolution) ? constraints.resolution : [constraints.resolution];
            if (!allowed.some((res) => String(res).toLowerCase() === resolution.toLowerCase())) {
              errors.push(`Resolution must be ${allowed.join(", ")} (current: ${resolution})`);
            }
          }
          resolve({ valid: errors.length === 0, errors: errors.length > 0 ? errors : undefined, dimensions: { width: img.naturalWidth, height: img.naturalHeight } });
        };
        img.onerror = () => resolve({ valid: true });
        img.src = src;
      }
    };
    reader.onerror = () => resolve({ valid: true });
    reader.readAsDataURL(file);
  });
};

const PlatformLimitStrip = ({ charLimits, currentLength }) => {
  if (!charLimits || typeof charLimits !== "object") return null;

  return (
    <div className="length-strip" style={{ marginTop: 6 }}>
      {Object.entries(charLimits).map(([platform, max]) => {
        const maxNum = Number(max);
        if (!Number.isFinite(maxNum)) return null;

        const status = currentLength > maxNum ? "err" : currentLength > Math.floor(maxNum * 0.9) ? "warn" : currentLength === 0 ? "" : "ok";
        const platformLabel = platform
          .replace(/[_-]/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return (
          <span key={platform} className={`length-chip${status ? " " + status : ""}`}>
            <span className="platform">{platformLabel}</span>
            {currentLength}/{maxNum}
          </span>
        );
      })}
    </div>
  );
};

const StoreListingSection = ({
  section,
  formValues = {},
  fieldMeta = {},
  updateField,
  handleUpload,
  handleRemoveFile,
  requestDelete,
  savingByKey = {},
  savedByKey = {},
  errorByKey = {},
  platformContext = {},
  setPlatformContext = () => { },
  isSuperAdmin = false,
  isFirstTime = false,
}) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);

  const subsections = useMemo(() => {
    const list = section?.subsections || [];
    return [...list].sort((a, b) => Number(a.subsection_order || 0) - Number(b.subsection_order || 0));
  }, [section?.subsections]);

  const requiredStats = useMemo(() => {
    const questions = subsections.flatMap((s) => s.questions || []);
    const required = questions.filter((q) => {
      if (isFirstTime) {
        return q.first_time_required === 1 || q.first_time_required === true;
      }
      return !!q.is_required;
    });
    const done = required.filter((q) => {
      const key = q.key;
      const meta = fieldMeta[key] || {};
      const value = resolvedValue(key, formValues, fieldMeta);
      if (UPLOAD_TYPES.has(q.input_type)) return getUploadedFiles(key, formValues, fieldMeta).length > 0;
      if (q.input_type === "yes_no") return value === "yes" || value === "no";
      return String(value || "").trim().length > 0;
    }).length;
    return { done, total: required.length };
  }, [subsections, fieldMeta, formValues, isFirstTime]);

  const createUpdateFieldWithPlatform = (field) => {
    const charLimitInfo = getCharLimitInfo(field);
    const isStoreField = /^store_/.test(field.key || "");

    return (value, immediate = false) => {
      const platform = isStoreField && charLimitInfo.smallestPlatform ? charLimitInfo.smallestPlatform : null;
      updateField(field, value, immediate, platform);
    };
  };

  const handleCopy = (key, text) => {
    const cleanText = String(text || "").replace(/<[^>]*>/g, "");
    navigator.clipboard.writeText(cleanText);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  if (!subsections.length) {
    return <div className="gob-empty-state">No store listing fields found.</div>;
  }

  return (
    <div>
      <div className="gob-breadcrumb">
        <span>Publisher Channel</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="current">Store Listing</span>
      </div>

      <div className="gob-page-head">
        <h1 className="gob-page-title">Store <span className="gradient">Listing</span></h1>
        <p className="gob-page-sub">All fields are rendered directly from API section/subsection/field metadata.</p>
      </div>

      <div className="readiness-strip">
        <span className="readiness-strip-label">Required completion</span>
        <span className={`platform-pill ${requiredStats.done > 0 ? "ok" : "empty"}`}>
          <span className="dot" />
          {requiredStats.done}/{requiredStats.total}
        </span>
      </div>

      <div className="flow-cards">
        {subsections.map((subsection, index) => {
          const questions = [...(subsection.questions || [])].sort((a, b) => Number(a.key_order || a.question_order || 0) - Number(b.key_order || b.question_order || 0));

          return (
            <div key={subsection.subsectionSlug || subsection.subsection_slug || subsection.subsectionName} className="flow-card">
              <div className="flow-card-head">
                <div className="flow-card-icon" style={{ background: ICON_COLORS[index % ICON_COLORS.length] }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <div className="flow-card-title-block">
                  <div className="flow-card-title">{subsection.subsectionName || subsection.subsection_slug || "Section"}</div>
                  <div className="flow-card-meta">{questions.length} field{questions.length === 1 ? "" : "s"}</div>
                </div>
              </div>

              {questions.map((q) => {
                const key = q.key;
                const meta = fieldMeta[key] || {};
                const value = resolvedValue(key, formValues, fieldMeta);
                const options = parseOptions(q.options);
                const charLimitInfo = getCharLimitInfo(q);
                const isStoreField = /^store_/.test(key);
                const currentLength = String(value || "").length;
                const overLimit = charLimitInfo.smallestLimit && currentLength > charLimitInfo.smallestLimit;
                const screenshotMode = isScreenshotQuestion(q);
                const files = getUploadedFiles(key, formValues, fieldMeta);
                const saving = !!savingByKey[key];
                const saved = !!savedByKey[key];
                const err = errorByKey[key] || "";
                const updateFieldWithPlatform = createUpdateFieldWithPlatform(q);
                const hasNonEmptyValue = !!String(value || "").trim() || files.length > 0 || !!meta.uploaded;

                return (
                  <div key={key} id={`q-row-${key}`} data-key={key} className="field-row">
                    <div className="field-main" style={{ gridTemplateColumns: "36px 1fr", padding: "12px 18px", alignItems: "start" }}>
                      <div />
                      <div className="field-info">
                        <div className="field-name" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                          <span>
                            {q.prompt || key}
                            {!!(isFirstTime ? (q.first_time_required === 1 || q.first_time_required === true) : q.is_required) && (
                              <span className="gob-req-tag" style={{ marginLeft: 6 }}>Required</span>
                            )}
                          </span>

                          {hasNonEmptyValue && (
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                              {(q.input_type === "textarea" || (!["textarea", "single_select", "yes_no"].includes(q.input_type) && !UPLOAD_TYPES.has(q.input_type))) && (
                                <button
                                  type="button"
                                  onClick={() => handleCopy(key, value)}
                                  className="gob-btn-icon"
                                  title="Copy text"
                                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: copiedKey === key ? "var(--gob-success, #10b981)" : "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                                >
                                  {copiedKey === key ? <CheckIcon style={{ width: 12, height: 12 }} /> : <CopyIcon style={{ width: 12, height: 12 }} />}
                                </button>
                              )}

                              {UPLOAD_TYPES.has(q.input_type) && !screenshotMode && files.length > 0 && (
                                <a
                                  href={files[0].url}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="gob-btn-icon"
                                  title="Download file"
                                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                                >
                                  <DownloadIcon style={{ width: 12, height: 12 }} />
                                </a>
                              )}

                              {q.input_type !== "yes_no" && requestDelete && !isSuperAdmin && (
                                <button
                                  type="button"
                                  disabled={saving}
                                  onClick={() => requestDelete(key)}
                                  className="gob-btn-icon"
                                  title="Clear value"
                                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "#ef4444", opacity: saving ? 0.5 : 1, transition: "opacity 0.15s" }}
                                >
                                  <TrashIcon style={{ width: 12, height: 12 }} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {q.tooltip && <div className="field-tooltip">{q.tooltip}</div>}

                        <div style={{ marginTop: 8 }}>
                          {q.input_type === "textarea" && (
                            <>
                              <textarea
                                className="field-textarea"
                                rows={4}
                                value={value}
                                onChange={(e) => updateFieldWithPlatform(e.target.value)}
                                onBlur={(e) => updateFieldWithPlatform(e.target.value, true)}
                                disabled={isSuperAdmin}
                              />
                              {isStoreField && charLimitInfo.limits && <PlatformLimitStrip charLimits={charLimitInfo.limits} currentLength={currentLength} />}
                              {overLimit && (
                                <div style={{ marginTop: 4, fontSize: 12, color: "#F43F5E" }}>
                                  ⚠ Exceeds limit for {charLimitInfo.smallestPlatform}. Please reduce text to save.
                                </div>
                              )}
                            </>
                          )}

                          {q.input_type === "single_select" && (
                            <select
                              className="field-text"
                              value={value}
                              onChange={(e) => updateFieldWithPlatform(e.target.value)}
                              onBlur={(e) => updateFieldWithPlatform(e.target.value, true)}
                              disabled={isSuperAdmin}
                            >
                              <option value="">Select an option</option>
                              {options.map((option) => {
                                const optionValue = typeof option === "object" ? option.value ?? option.label : option;
                                const optionLabel = typeof option === "object" ? option.label ?? option.value : option;
                                return (
                                  <option key={String(optionValue)} value={optionValue}>
                                    {optionLabel}
                                  </option>
                                );
                              })}
                            </select>
                          )}

                          {q.input_type === "yes_no" && (
                            <div className="q-options" style={{ justifyContent: "flex-start" }}>
                              <button type="button" className={`q-option ${value === "yes" ? "selected yes" : ""}`} disabled={isSuperAdmin} onClick={() => !isSuperAdmin && updateFieldWithPlatform("yes", true)}>Yes</button>
                              <button type="button" className={`q-option ${value === "no" ? "selected no" : ""}`} disabled={isSuperAdmin} onClick={() => !isSuperAdmin && updateFieldWithPlatform("no", true)}>No</button>
                            </div>
                          )}

                          {UPLOAD_TYPES.has(q.input_type) && (
                            <div>
                              {files.length > 0 && (
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                                  {files.map((file) => (
                                    <div key={file.url} style={{ position: "relative", display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
                                      <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ display: "block", textDecoration: "none" }}>
                                        {q.input_type === "video" ? (
                                          <video src={file.url} style={{ maxHeight: 80, maxWidth: 160, borderRadius: 6 }} controls />
                                        ) : (
                                          <img src={file.url} alt={q.prompt || key} style={{ maxHeight: 80, maxWidth: 160, borderRadius: 6, objectFit: "cover" }} />
                                        )}
                                      </a>

                                      <a
                                        href={file.url}
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Download file"
                                        style={{
                                          position: "absolute", top: -6, left: -6,
                                          width: 22, height: 22, borderRadius: "50%",
                                          background: "var(--gob-primary, #6366f1)", color: "white", border: "2px solid white",
                                          cursor: "pointer",
                                          display: "flex", alignItems: "center", justifyContent: "center",
                                          boxShadow: "0 1px 4px rgba(0,0,0,.25)",
                                          padding: 0, zIndex: 5
                                        }}
                                      >
                                        <DownloadIcon style={{ width: 11, height: 11 }} />
                                      </a>

                                      {handleRemoveFile && !isSuperAdmin && (
                                        <button
                                          type="button"
                                          disabled={saving}
                                          onClick={(e) => { e.stopPropagation(); handleRemoveFile(key, file.url, file.value_id); }}
                                          title="Remove this file"
                                          style={{
                                            position: "absolute", top: -6, right: -6,
                                            width: 22, height: 22, borderRadius: "50%",
                                            background: "#dc2626", color: "white", border: "2px solid white",
                                            cursor: saving ? "not-allowed" : "pointer",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            opacity: saving ? 0.5 : 1,
                                            boxShadow: "0 1px 4px rgba(0,0,0,.25)",
                                            padding: 0, zIndex: 5
                                          }}
                                        >
                                          <TrashIcon style={{ width: 11, height: 11 }} />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {q.media_constraints && (
                                <div style={{ marginBottom: 12, padding: 10, backgroundColor: "var(--gob-bg-soft)", borderRadius: 6, fontSize: 12, color: "var(--gob-ink-2)" }}>
                                  {q.media_constraints.resolution && (
                                    <div>Resolution: {Array.isArray(q.media_constraints.resolution) ? q.media_constraints.resolution.join(" or ") : q.media_constraints.resolution}</div>
                                  )}
                                  {q.media_constraints.aspect_ratio && (
                                    <div>Aspect Ratio: {q.media_constraints.aspect_ratio}</div>
                                  )}
                                </div>
                              )}

                              {!isSuperAdmin ? (
                                <label className="gob-btn" style={{ fontSize: 12, cursor: "pointer" }}>
                                  {saving ? "Uploading..." : files.length > 0 ? (screenshotMode ? "Add more" : "Replace") : (screenshotMode ? "Upload image(s)" : "Upload")}
                                  <input
                                    type="file"
                                    accept={q.input_type === "video" ? "video/*" : "image/*"}
                                    multiple={screenshotMode}
                                    style={{ display: "none" }}
                                    disabled={saving}
                                    onChange={async (e) => {
                                      const selected = Array.from(e.target.files || []);
                                      e.target.value = "";
                                      if (selected.length === 0) return;
                                      const validFiles = [];
                                      for (const file of selected) {
                                        const validation = await validateMediaDimensions(file, q);
                                        if (!validation.valid) {
                                          setValidationErrors((prev) => ({ ...prev, [key]: validation.errors?.join("; ") || "Invalid media" }));
                                          setTimeout(() => setValidationErrors((prev) => ({ ...prev, [key]: "" })), 4000);
                                          return;
                                        }
                                        validFiles.push(file);
                                      }
                                      if (validFiles.length > 0) {
                                        setValidationErrors((prev) => ({ ...prev, [key]: "" }));
                                        if (screenshotMode) handleUpload(q, validFiles);
                                        else handleUpload(q, validFiles[0]);
                                      }
                                    }}
                                  />
                                </label>
                              ) : files.length === 0 ? (
                                <span style={{ fontSize: 12, color: "var(--gob-ink-3)" }}>No files uploaded</span>
                              ) : null}
                            </div>
                          )}

                          {!["textarea", "single_select", "yes_no"].includes(q.input_type) && !UPLOAD_TYPES.has(q.input_type) && (
                            <>
                              <input
                                className="field-text"
                                type={q.input_type === "email" ? "email" : q.input_type === "url" ? "url" : "text"}
                                value={value}
                                onChange={(e) => updateFieldWithPlatform(e.target.value)}
                                onBlur={(e) => updateFieldWithPlatform(e.target.value, true)}
                                disabled={isSuperAdmin}
                              />
                              {isStoreField && charLimitInfo.limits && <PlatformLimitStrip charLimits={charLimitInfo.limits} currentLength={currentLength} />}
                              {overLimit && (
                                <div style={{ marginTop: 4, fontSize: 12, color: "#F43F5E" }}>
                                  ⚠ Exceeds limit for {charLimitInfo.smallestPlatform}. Please reduce text to save.
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {saving && <div className="q-help" style={{ marginTop: 4 }}>Saving...</div>}
                        {validationErrors[key] && (
                          <div style={{ marginTop: 4, fontSize: 12, color: "var(--gob-error)" }}>
                            *{validationErrors[key]}
                          </div>
                        )}
                        {saved && <div className="q-help" style={{ marginTop: 4, color: "var(--gob-success)" }}>Saved ✓</div>}
                        {err && <div style={{ marginTop: 4, fontSize: 12, color: "var(--gob-error)" }}>{err}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { StoreListingSection, getCharLimitInfo };
export default StoreListingSection;
